import { useState, useEffect } from 'react';
import { Button } from '~/components/button';
import { ErrorMessage } from '~/components/error-message';
import { HelpText } from '~/components/help-text';
import { type ImageType, ImageSelector, GIFFORD_SOLAR } from '~/components/image';
import { Container, TwoColumnGrid } from '~/components/layout';
import { ReviewBlock } from '~/components/review-block';
import { Separator } from '~/components/separator';
import { Description, Title } from '~/components/typography';
import { useCopy } from '~/hooks';
import { BillingDataSource, Utility } from '~/typings/context';
import { CALENDLY_URI_API, CALNEDLY_FETCH_DATA_API_BUILDER, IS_TEST } from '~/utils/constants';
import { trackError } from '~/utils/tracking';
import { fetchUriMock, fetchDataMock } from './smb-terms.utils';

export const SmbTerms = ({
  context,
  error,
  loading,
  next,
  overrideCopy,
  pending,
}: SmbTermsProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const { billingSelection, paymentMethod, email } = context;
  const encodedEmail = encodeURIComponent(email);

  const [userUri, setUserUri] = useState('');
  const [orgUri, setOrgUri] = useState('');
  const [scheduledStartTime, setscheduledStartTime] = useState('');
  const [scheduledEndTime, setscheduledEndTime] = useState('');
  const [formattedTime, setFormattedTime] = useState('');

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${process.env.CALENDLY_API_KEY}`,
    },
  };

  const fetchUri = async () => {
    try {
      const response = await fetch(CALENDLY_URI_API, options);
      const jsonResponse = IS_TEST ? fetchUriMock : await response.json();
      setUserUri(encodeURIComponent(jsonResponse.resource.uri));
      setOrgUri(encodeURIComponent(jsonResponse.resource.current_organization));
    } catch (err) {
      trackError(new Error(err.title), {
        extras: { message: err.message || err.title, details: err.details },
      });
    }
  };

  const fetchData = async (userUri, orgUri, encodedEmail) => {
    try {
      const response = await fetch(
        CALNEDLY_FETCH_DATA_API_BUILDER(userUri, orgUri, encodedEmail),
        options,
      );
      const jsonResponse = IS_TEST ? fetchDataMock : await response.json();
      setscheduledStartTime(jsonResponse.collection[0].start_time);
      setscheduledEndTime(jsonResponse.collection[0].end_time);
    } catch (err) {
      trackError(new Error(err.title), {
        extras: { message: err.message || err.title, details: err.details },
      });
    }
  };

  useEffect(() => {
    fetchUri();
  }, []);

  useEffect(() => {
    if (!userUri || !orgUri) return;
    fetchData(userUri, orgUri, encodedEmail);
  }, [userUri, orgUri]);

  useEffect(() => {
    if (!scheduledStartTime || !scheduledEndTime) return;

    const startTime = new Date(scheduledStartTime);
    const endTime = new Date(scheduledEndTime);

    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const formattedStartTime = timeFormatter.format(startTime);
    const formattedEndTime = timeFormatter.format(endTime);
    const formattedDate = dateFormatter.format(startTime);

    const formattedDateAndTime = IS_TEST
      ? '7:15 PM - 7:45 PM, Saturday, August 24, 2019'
      : `${formattedStartTime} - ${formattedEndTime}, ${formattedDate}`;
    setFormattedTime(formattedDateAndTime);
  }, [scheduledStartTime, scheduledEndTime]);

  return (
    <TwoColumnGrid testId="screen-SmbTerms">
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description mb={6}>{copy.description}</Description>
        <Separator pt={0} />
        <ReviewBlock layout="column">
          <ReviewBlock.Item label={copy.savingsBenefitSectionTitle}>
            {copy.savingsBenefit}
          </ReviewBlock.Item>
          <ReviewBlock.Item label={copy.utilityBillSectionTitle}>
            {copy.utilityBill}
          </ReviewBlock.Item>
        </ReviewBlock>
        <Container mt={6}>
          <HelpText isSmb />
        </Container>
        <Separator />
        <ReviewBlock>
          {context.businessName && (
            <ReviewBlock.Item label="Business name" notranslate>
              {context.businessName}
            </ReviewBlock.Item>
          )}
          <ReviewBlock.Item
            label="Name"
            notranslate
          >{`${context.firstName} ${context.lastName}`}</ReviewBlock.Item>
          <ReviewBlock.Item label="Email" notranslate>
            {context.email}
          </ReviewBlock.Item>
          {!!(paymentMethod && billingSelection !== 'ucb') && (
            <ReviewBlock.Item label="Payment method" notice={copy.notice}>
              {`${paymentMethod.source} ending in •••• ${paymentMethod.lastFour}`}
            </ReviewBlock.Item>
          )}
          {formattedTime !== '' && (
            <>
              <Separator />
              <ReviewBlock.Item label="Welcome call" notranslate>
                {formattedTime}
              </ReviewBlock.Item>
            </>
          )}
        </ReviewBlock>
        <Separator />
        <ErrorMessage error={error} my={3} />
        <Button
          mode="primary"
          onClick={next}
          disabled={loading}
          loading={pending}
          testId="submit-smb-terms-button"
        >
          {copy.next}
        </Button>
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const defaultCopy = (context: SmbTermsContext) => {
  const paymentNotice =
    context.billingSelection === 'dual'
      ? "You'll start seeing statements from us once we assign you to an active solar farm. Expect net savings from the credits you earn, minus your subscription fee."
      : `You will not be charged today. When your next bill from ${context.utility.displayName} is ready, we'll send you the details and charge your payment method on your account three days later.`;

  return {
    title: 'Finally, review these details.',
    description:
      "After we verify your information, we'll connect your business with a community solar farm.",
    imageType: GIFFORD_SOLAR as ImageType,
    next: 'Looks good to me!',
    notice: paymentNotice,
    savingsBenefitSectionTitle: 'Your savings',
    savingsBenefit:
      "Once your business is connected to a solar farm, you'll see guaranteed savings on the solar credits applied to your power bill.",
    utilityBillSectionTitle: 'Your bill',
    utilityBill:
      "You'll pay your business's utility bill automatically through our secure platform.",
  };
};

export type SmbTermsCopy = ReturnType<typeof defaultCopy>;

export interface SmbTermsProps {
  context: SmbTermsContext;
  error?: string;
  loading: boolean;
  next: () => void;
  overrideCopy?: (context: SmbTermsContext) => Partial<SmbTermsCopy>;
  pending: boolean;
}

export interface SmbTermsContext {
  billingSelection?: BillingDataSource;
  businessName?: string;
  email: string;
  firstName: string;
  lastName: string;
  paymentMethod?: { lastFour: string; source: string };
  utility: Utility;
}
